import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Search, RefreshCw, Heart } from "lucide-react";
import Navigation from "./Navigation";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { useFetch } from "../hooks/useApi";
import { convertUTCToMeetingTimezone } from "../utils/dateTimeUtils";
import { apiService } from "../services/apiService";
import MeetingCard from "./ui/MeetingCard";
import { getPrimaryImageUrl } from "../utils/imageUtils";

const MySavedRunsPage = () => {
  const { user, loading } = useAuth();
  const [meetings, setMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const cardsPerPage = 6;

  // Fetch user's saved runs
  const { data: savedRuns, loading: savedRunsLoading, refetch: refetchSavedRuns } = useFetch(
    () => apiService.meetings.getMySavedRuns(),
    [],
    { enabled: !!user }
  );

  // Fetch full meeting data for saved meetings
  const fetchSavedMeetings = useCallback(async () => {
    if (!savedRuns || !Array.isArray(savedRuns) || savedRuns.length === 0) {
      setMeetings([]);
      return;
    }

    setLoadingMeetings(true);
    try {
      // Group saved runs by meeting_id
      const savedRunMap = new Map();
      const uniqueMeetingIds = new Set();

      savedRuns.forEach((savedRun) => {
        const meetingId = savedRun.meeting_id;
        if (meetingId) {
          uniqueMeetingIds.add(meetingId);
          if (!savedRunMap.has(meetingId)) {
            savedRunMap.set(meetingId, []);
          }
          savedRunMap.get(meetingId).push(savedRun);
        }
      });

      // Fetch full meeting data for each unique meeting_id
      const meetingPromises = Array.from(uniqueMeetingIds).map(async (meetingId) => {
        try {
          // Suppress 404 errors since deleted meetings are expected
          const meeting = await apiService.meetings.getById(meetingId, true);
          
          // Validate that the meeting exists and has required fields
          // If meeting is undefined, null, or missing required fields, skip it
          if (!meeting || !meeting.id || !meeting.club_id) {
            return null;
          }
          
          const meetingSavedRuns = savedRunMap.get(meetingId) || [];
          
          // Get the most recent saved run
          const latestSavedRun = meetingSavedRuns.reduce((latest, current) => {
            const currentDate = current.saved_at || current.savedAt;
            const latestDate = latest?.saved_at || latest?.savedAt;
            if (!latestDate) return current;
            if (!currentDate) return latest;
            return new Date(currentDate) > new Date(latestDate) ? current : latest;
          }, null);

          return {
            ...meeting,
            userSavedRun: {
              id: latestSavedRun?.id,
              saved_at: latestSavedRun?.saved_at || latestSavedRun?.savedAt,
            },
          };
        } catch (err) {
          // Silently skip meetings that can't be fetched (deleted, 404, etc.)
          // 404s are expected when meetings have been deleted
          // Only log non-404 errors as those might indicate real issues
          if (err.response?.status && err.response.status !== 404) {
            console.error(`Error fetching meeting ${meetingId}:`, err);
          }
          return null;
        }
      });

      const fetchedMeetings = await Promise.all(meetingPromises);
      // Filter out null/undefined and ensure meetings have required fields
      const validMeetings = fetchedMeetings.filter(meeting => 
        meeting !== null && 
        meeting !== undefined && 
        meeting.id && 
        meeting.club_id
      );
      
      setMeetings(validMeetings);
    } catch (err) {
      console.error('Error fetching saved meetings:', err);
      toast.error('Failed to load saved runs');
      setMeetings([]);
    } finally {
      setLoadingMeetings(false);
    }
  }, [savedRuns]);

  // Fetch meetings when saved runs data changes
  useEffect(() => {
    if (savedRuns !== undefined) {
      fetchSavedMeetings();
    }
  }, [savedRuns, fetchSavedMeetings]);

  // Helper function to format date in meeting timezone
  const formatDateInMeetingTimezone = (scheduledDate, meetingTimezone = null) => {
    if (!scheduledDate) return '';
    
    try {
      const meetingDate = convertUTCToMeetingTimezone(scheduledDate, meetingTimezone);
      if (!meetingDate || isNaN(meetingDate.getTime())) {
        return '';
      }
      
      return meetingDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: meetingTimezone || undefined
      });
    } catch (error) {
      return '';
    }
  };

  // Helper function to format time to 12-hour format
  const formatTimeTo12Hour = (time24) => {
    if (!time24) return '';
    
    if (time24.includes('AM') || time24.includes('PM')) {
      return time24;
    }
    
    const [hours, minutes] = time24.split(':');
    const hour24 = parseInt(hours);
    const mins = minutes || '00';
    
    const period = hour24 >= 12 ? 'PM' : 'AM';
    const displayHours = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    return `${displayHours}:${mins} ${period}`;
  };

  // Helper function to format UTC datetime to meeting timezone (12-hour time)
  const formatScheduledDateToLocalTime = (scheduledDate, meetingTimezone = null) => {
    if (!scheduledDate) return '';
    
    try {
      const meetingDate = convertUTCToMeetingTimezone(scheduledDate, meetingTimezone);
      if (!meetingDate || isNaN(meetingDate.getTime())) {
        return '';
      }
      
      const hours = meetingDate.getHours();
      const minutes = meetingDate.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    } catch (error) {
      return '';
    }
  };

  // Helper function to parse scheduled date
  const parseScheduledDate = (scheduledDate) => {
    if (!scheduledDate) {
      throw new Error('Missing scheduled date');
    }
    
    const date = new Date(scheduledDate);
    
    if (isNaN(date.getTime())) {
      throw new Error('Invalid scheduled date format');
    }
    
    return date;
  };

  const filterRunsBySearch = (runs) => {
    if (!runs || !searchTerm.trim()) {
      return runs || [];
    }
    
    const searchLower = searchTerm.toLowerCase();
    return runs.filter(meeting => {
      const clubName = meeting.club_name?.toLowerCase() || meeting.clubName?.toLowerCase() || '';
      const location = meeting.address?.toLowerCase() || '';
      const clubType = meeting.club_type?.toLowerCase() || meeting.clubType?.toLowerCase() || '';
      const scheduledDate = meeting.scheduled_date?.toLowerCase() || meeting.scheduledDate?.toLowerCase() || '';
      
      return clubName.includes(searchLower) || 
             location.includes(searchLower) || 
             clubType.includes(searchLower) ||
             scheduledDate.includes(searchLower);
    });
  };

  // Get paginated data
  const getPaginatedData = (data) => {
    if (!data) return [];
    const startIndex = (currentPage - 1) * cardsPerPage;
    const endIndex = startIndex + cardsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = (data) => {
    if (!data) return 0;
    return Math.ceil(data.length / cardsPerPage);
  };

  // Render meeting card component
  const renderRunCard = (meeting) => {
    let meetingDate = null;
    let meetingDateString = null;
    let isToday = false;
    
    try {
      if (meeting.scheduled_date || meeting.scheduledDate) {
        const scheduledDate = meeting.scheduled_date || meeting.scheduledDate;
        meetingDate = parseScheduledDate(scheduledDate);
        const now = new Date();
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const meetingDay = new Date(meetingDate.getFullYear(), meetingDate.getMonth(), meetingDate.getDate());
        isToday = meetingDay.getTime() === today.getTime();
        
        meetingDateString = formatDateInMeetingTimezone(scheduledDate, meeting.timezone);
      }
    } catch (error) {
      console.warn('Error parsing scheduled date:', error);
    }

    const primaryImage = getPrimaryImageUrl(
      meeting.images || meeting.image,
      meeting.image || meeting.club_image || "https://images.unsplash.com/photo-1598012268972-217e6036c419?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxydW5uaW5nJTIwY29tbXVuaXR5fGVufDB8fHx8MTc1ODIxMTI1Mnww&ixlib=rb-4.1.0&q=85"
    );
    
    return (
      <MeetingCard
        key={meeting.id}
        event={{
          id: meeting.id,
          club_id: meeting.club_id,
          name: meeting.name || meeting.meeting_title || meeting.club_name,
          description: meeting.what_to_expect || '',
          image: primaryImage,
          imagePosition: meeting.image_position || meeting.imagePosition,
          time: meeting.scheduled_date ? formatScheduledDateToLocalTime(meeting.scheduled_date, meeting.timezone || null) : (meeting.scheduled_time || 'Time TBD'),
          attendees: meeting.attendees || meeting.participant_count || 0,
          location: meeting.address || meeting.city,
          date: meetingDateString || 'Scheduled',
          difficulty: meeting.difficulty || meeting.club_type,
          timeUntil: isToday ? 'Today' : meetingDateString || 'Scheduled',
          is_recurring: meeting.is_recurring || meeting.isRecurring,
          recurrence_pattern: meeting.recurrence_pattern || meeting.recurrencePattern
        }}
        onButtonClick={() => {
          // Navigate to run details or club page
          if (meeting.club_id) {
            window.location.href = `/runclubs?club=${meeting.club_id}`;
          }
        }}
        buttonText="View Details"
        showHover={false}
      />
    );
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-runcards-background">
        <Navigation />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-runcards-text-secondary">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // If no user after loading, redirect handled in useEffect
  if (!user) {
    return null;
  }

  const filteredMeetings = filterRunsBySearch(meetings);
  const paginatedMeetings = getPaginatedData(filteredMeetings);
  const totalPages = getTotalPages(filteredMeetings);

  return (
    <div className="min-h-screen bg-runcards-background text-runcards-text-primary">
      <style>{`
        button:focus,
        a:focus,
        input:focus {
          outline: none !important;
          box-shadow: none !important;
        }
        button:focus-visible,
        a:focus-visible,
        input:focus-visible {
          outline: none !important;
          box-shadow: none !important;
        }
      `}</style>
      <Navigation />
      
      {/* Hero Section with Background Image */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: "url('/Marcus_Speed_and_Form.png')",
            filter: "grayscale(100%) brightness(0.6)"
          }}
        />
        <div className="absolute inset-0 bg-runcards-background/30" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 max-w-6xl mx-auto pt-44 sm:pt-52 lg:pt-60">
          {/* Main Title */}
          <div className="mb-6">
            <h2 className="text-runcards-title sm:text-4xl md:text-5xl lg:text-6xl font-runcards-bold mb-4 text-runcards-text-primary w-full" style={{letterSpacing: '-0.02em', transform: 'scaleX(1.5)', whiteSpace: 'nowrap'}}>
              MY SAVED RUNS
            </h2>
            <p className="text-runcards-xl sm:text-runcards-xxl font-runcards text-runcards-text-tertiary">
              View all the runs you've saved and want to join.
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-8">
            <div className="relative">
              <div className="flex">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-runcards-text-secondary w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search by club name or run details..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1); // Reset to first page on search
                    }}
                    className="pl-12 pr-4 py-4 text-runcards-lg font-runcards bg-runcards-surface border-runcards-border/20 text-runcards-text-primary placeholder:text-runcards-text-secondary focus:bg-runcards-foreground focus:border-runcards-border/40 rounded-l-runcards-lg rounded-r-none"
                  />
                </div>
                <Button
                  onClick={() => {
                    refetchSavedRuns();
                  }}
                  className="bg-runcards-foreground hover:bg-white/30 text-runcards-text-primary border border-runcards-border/20 px-6 py-4 rounded-r-xl rounded-l-none"
                >
                  <RefreshCw className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Runs List */}
      <section className="py-16 px-4 sm:px-6 bg-runcards-background">
        <div className="max-w-7xl mx-auto">
          {savedRunsLoading || loadingMeetings ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-2 border-runcards-border/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-runcards-text-secondary">Loading your saved runs...</p>
            </div>
          ) : filteredMeetings.length === 0 ? (
            <div className="text-center py-16">
              <Heart className="w-16 h-16 mx-auto mb-4 text-runcards-text-tertiary opacity-50" />
              <h3 className="text-runcards-xl font-runcards-bold text-runcards-text-primary mb-2">
                {searchTerm ? 'No matching runs found' : 'No saved runs'}
              </h3>
              <p className="text-runcards-text-secondary mb-6">
                {searchTerm 
                  ? 'Try adjusting your search terms'
                  : "You haven't saved any runs yet. Check out the Runs tab to find runs you'd like to save!"
                }
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => window.location.href = '/run'}
                  className="bg-white text-black hover:bg-gray-100 px-6 py-3 rounded-xl font-runcards-bold"
                >
                  Browse Runs
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="mb-6 text-center">
                <p className="text-runcards-text-secondary">
                  Showing {paginatedMeetings.length} of {filteredMeetings.length} saved {filteredMeetings.length === 1 ? 'run' : 'runs'}
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                {paginatedMeetings.map((meeting) => renderRunCard(meeting))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center space-x-2 mt-8">
                  <Button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="bg-runcards-foreground hover:bg-white/30 text-runcards-text-primary border border-runcards-border/20 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </Button>
                  
                  <span className="text-runcards-text-secondary px-4">
                    Page {currentPage} of {totalPages}
                  </span>
                  
                  <Button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="bg-runcards-foreground hover:bg-white/30 text-runcards-text-primary border border-runcards-border/20 px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default MySavedRunsPage;

